<template>
  <div class="pixso-page industry-products">
    <main class="industry-products__main">
      <section class="industry-products__hero" aria-labelledby="industry-products-title">
        <img class="industry-products__hero-bg" :src="heroImage" alt="" aria-hidden="true" />
        <div class="industry-products__hero-copy">
          <h1 id="industry-products-title" class="industry-products__title">
            <span>{{ t('IndustryIndex.hero.line1') }}</span>
            <span>{{ t('IndustryIndex.hero.line2') }}</span>
          </h1>
          <p class="industry-products__desc">{{ t('IndustryIndex.hero.description') }}</p>
        </div>
      </section>

      <section class="industry-products__series" :aria-label="t('IndustryIndex.seriesOverview.title')">
        <div class="industry-products__series-grid">
          <router-link
            class="industry-products__series-card"
            to="/product/industry/positioning"
            :aria-label="t('IndustryIndex.seriesOverview.items.positioning.title')"
          >
            <img class="industry-products__series-card-image" :src="positioningSeriesImage" :alt="t('IndustryIndex.seriesOverview.items.positioning.alt')" />
            <div class="industry-products__series-card-copy">
              <h2>{{ t('IndustryIndex.seriesOverview.items.positioning.title') }}</h2>
              <span>{{ t('IndustryIndex.seriesOverview.link') }} &gt;</span>
            </div>
          </router-link>
          <router-link
            class="industry-products__series-card"
            to="/product/industry/domain-control"
            :aria-label="t('IndustryIndex.seriesOverview.items.domainControl.title')"
          >
            <img class="industry-products__series-card-image" :src="domainControlSeriesImage" :alt="t('IndustryIndex.seriesOverview.items.domainControl.alt')" />
            <div class="industry-products__series-card-copy">
              <h2>{{ t('IndustryIndex.seriesOverview.items.domainControl.title') }}</h2>
              <span>{{ t('IndustryIndex.seriesOverview.link') }} &gt;</span>
            </div>
          </router-link>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n'

import heroImage from '@/assets/images/pixso-industry-official/industry-robot.jpg'
import positioningSeriesImage from '@/assets/images/pixso-industry-official/positioning-series.jpg'
import domainControlSeriesImage from '@/assets/images/pixso-industry-official/domain-control-series.jpg'

const { t } = useI18n()
</script>

<style scoped>
.industry-products {
  --industry-index-hero-copy-max: 1200px;
  --industry-index-hero-desc-max: 1036px;
  --industry-index-hero-mobile-bg-width: var(--pixso-page-max);
  --industry-index-card-height: 811px;
  --industry-index-card-height-mobile: 420px;
  --industry-index-card-radius-mobile: var(--pixso-radius-md);
  --industry-index-card-bg: var(--pixso-color-bg);
  --industry-index-card-overlay: linear-gradient(180deg, rgba(0, 0, 0, 0) 56%, rgba(0, 0, 0, 0.82) 100%);
  --industry-index-series-bottom-tablet: 140px;
  --industry-index-series-bottom-mobile: 120px;
  --industry-index-title-mobile-size: var(--pixso-fs-36);
  --industry-index-title-mobile-line: var(--pixso-lh-36);
  --industry-index-card-title-mobile-size: var(--pixso-fs-28);
  --industry-index-card-link-mobile-size: var(--pixso-fs-14);
}

.industry-products__main {
  width: 100%;
  background: var(--pixso-color-bg);
}

.industry-products__hero {
  width: 100%;
  height: var(--pixso-hero-height);
  min-height: var(--pixso-hero-min);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--pixso-color-bg);
}

.industry-products__hero-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 25% center;
}

.industry-products__hero-copy {
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--industry-index-hero-copy-max));
  padding: 0 var(--pixso-fs-96);
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--pixso-gap-md);
  text-align: center;
}

.industry-products__title {
  width: 100%;
  margin: 0;
  display: flex;
  flex-direction: column;
  color: var(--pixso-color-text);
  font-size: var(--pixso-fs-96);
  font-weight: 700;
  line-height: var(--pixso-lh-96);
  letter-spacing: 0;
}

.industry-products__desc {
  width: var(--industry-index-hero-desc-max);
  max-width: calc(100vw - var(--pixso-page-padding) * 2);
  margin: 0;
  color: var(--pixso-color-text);
  font-size: var(--pixso-fs-20);
  font-weight: 700;
  line-height: var(--pixso-lh-20);
  letter-spacing: 0;
}

.industry-products__series {
  width: min(100%, var(--pixso-page-max));
  margin: 0 auto;
  padding: var(--pixso-fs-96) 0 var(--pixso-section-gap-xl);
  background: var(--pixso-color-bg);
}

.industry-products__series-grid {
  width: calc(100% - var(--pixso-page-padding) * 2);
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--pixso-gap-md);
}

.industry-products__series-card {
  position: relative;
  display: block;
  min-width: 0;
  height: var(--industry-index-card-height);
  overflow: hidden;
  border-radius: var(--pixso-radius-lg);
  color: var(--pixso-color-text);
  text-decoration: none;
  background: var(--industry-index-card-bg);
  isolation: isolate;
}

.industry-products__series-card::after {
  position: absolute;
  inset: 0;
  z-index: 1;
  content: "";
  background: var(--industry-index-card-overlay);
  pointer-events: none;
}

.industry-products__series-card-image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.industry-products__series-card-copy {
  position: absolute;
  right: var(--pixso-gap-lg);
  bottom: var(--pixso-page-padding);
  left: var(--pixso-gap-lg);
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--pixso-gap-sm);
  text-align: center;
}

.industry-products__series-card-copy h2 {
  margin: 0;
  color: var(--pixso-color-text);
  font-size: var(--pixso-fs-36);
  font-weight: 700;
  line-height: var(--pixso-lh-36);
}

.industry-products__series-card-copy span {
  color: var(--pixso-color-text);
  font-size: var(--pixso-fs-16);
  font-weight: 700;
  line-height: var(--pixso-lh-16);
}

@media (max-width: 1200px) {
  .industry-products__hero {
    height: var(--pixso-hero-height);
    min-height: var(--pixso-hero-min);
  }

  .industry-products__hero-copy {
    padding: 0 var(--pixso-fs-32);
  }

  .industry-products__title {
    font-size: var(--pixso-fs-72);
    line-height: var(--pixso-lh-72);
  }

  .industry-products__series {
    padding-top: var(--pixso-fs-96);
    padding-bottom: var(--industry-index-series-bottom-tablet);
  }
}

@media (max-width: 768px) {
  .industry-products__hero {
    height: var(--pixso-hero-mobile);
    min-height: var(--pixso-hero-mobile);
  }

  .industry-products__hero-bg {
    width: 100%;
  }

  .industry-products__hero-copy {
    padding: 0;
    gap: var(--pixso-fs-16);
  }

  .industry-products__title {
    font-size: var(--pixso-fs-36);
    line-height: var(--pixso-lh-36);
  }

  .industry-products__desc {
    font-size: var(--pixso-fs-14);
    line-height: var(--pixso-lh-14);
  }

  .industry-products__series {
    padding-top: var(--pixso-fs-72);
    padding-bottom: var(--industry-index-series-bottom-mobile);
  }

  .industry-products__series-grid {
    grid-template-columns: 1fr;
    gap: var(--pixso-fs-16);
    width: calc(100% - var(--pixso-page-padding) * 2);
    margin: 0 auto;
  }

  .industry-products__series-card {
    height: var(--industry-index-card-height-mobile);
    border-radius: var(--industry-index-card-radius-mobile);
  }

  .industry-products__series-card-copy {
    right: var(--pixso-gap-md);
    bottom: var(--pixso-fs-32);
    left: var(--pixso-gap-md);
  }

  .industry-products__series-card-copy h2 {
    font-size: var(--pixso-fs-28);
  }

  .industry-products__series-card-copy span {
    font-size: var(--pixso-fs-14);
  }
}
</style>
